import { parse } from 'date-fns';
import type {
  CampaignDraftScheduleApi,
  UpdateCampaignRequestApi,
} from '../types/campaigns-api.types';
import {
  buildScheduleRecurrence,
  mapApiFrequency,
  utcIsoToWallClockInZone,
  wallClockInZoneToUtcIso,
  type ScheduleWizardFrequency,
} from './schedule-campaign.utils';

export type CampaignScheduleDraft = {
  sendType: 'Scheduled' | 'Recurring';
  /** yyyy-MM-dd of the schedule / recurring start day. */
  date: string;
  /** HH:mm wall-clock time in `timeZone`. */
  time: string;
  timeZone: string;
  recurringFrequency: ScheduleWizardFrequency;
};

/**
 * Wizard schedule → PATCH `draftSchedule` (Campaigns_32). The campaign stays a draft; the server
 * keeps the plan apart from the real schedule, so nothing sends until the Review step submits it.
 * `scheduledAt` is sent for both modes so a recurring plan restores its start day and time too.
 */
export const toDraftScheduleRequestApi = (
  draft: CampaignScheduleDraft
): NonNullable<UpdateCampaignRequestApi['draftSchedule']> => {
  const day = parse(draft.date, 'yyyy-MM-dd', new Date());
  const scheduledAt = wallClockInZoneToUtcIso(day, draft.time, draft.timeZone);

  if (draft.sendType === 'Scheduled') {
    return { mode: 'scheduled', scheduledAt, timeZone: draft.timeZone };
  }

  return {
    mode: 'recurring',
    scheduledAt,
    timeZone: draft.timeZone,
    recurrence: buildScheduleRecurrence(draft.recurringFrequency, day, draft.time, draft.timeZone),
  };
};

/** `campaign.draftSchedule` → wizard schedule, or `null` when there is none or it is unreadable. */
export const fromDraftScheduleApi = (
  api: CampaignDraftScheduleApi | null | undefined
): CampaignScheduleDraft | null => {
  if (!api) return null;

  const mode = api.mode?.trim().toLowerCase();
  if (mode !== 'scheduled' && mode !== 'recurring') return null;

  const timeZone = api.timeZone?.trim() || api.recurrence?.timeZone?.trim() || 'UTC';
  const wallClock = api.scheduledAt ? utcIsoToWallClockInZone(api.scheduledAt, timeZone) : null;
  const date = wallClock?.date ?? api.recurrence?.startDate?.slice(0, 10);
  const time = wallClock?.time ?? api.recurrence?.timeOfDay?.trim();
  if (!date || !time) return null;

  return {
    sendType: mode === 'scheduled' ? 'Scheduled' : 'Recurring',
    date,
    time,
    timeZone,
    recurringFrequency: mapApiFrequency(api.recurrence?.frequency, api.recurrence?.interval),
  };
};
